import React from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { FileText, Tag, Boxes, Sparkles, ArrowRight } from 'lucide-react';

export default function AsaPrintChoiceModal({
  isOpen,
  onClose,
  order,
  shipment,
  onSelectSuratJalan,
  onSelectLabelDus,
  onSelectBoth
}) {
  if (!isOpen) return null;

  const brandName = (order?.brand_name || order?.product_name || 'Brand').split(' / ')[0].trim();
  const destName = shipment?._index !== undefined 
    ? `Destinasi ${shipment._index + 1}` 
    : (shipment?.penerima || 'Pengiriman Utama');
  const ekspedisi = shipment?.shipping_via || order?.shipping_via || order?.courier || 'Ekspedisi';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden border-[#E6D5B8] bg-white shadow-2xl rounded-3xl">
        <div className="p-6">
          {/* Asa Avatar & Profile Header */}
          <div className="flex flex-col items-center justify-center text-center mb-4">
            <div className="relative mb-2.5">
              <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white shadow-md relative z-10 bg-[#F9F5EC]">
                <img 
                  src="/asa-profile.png" 
                  alt="Asa Satala" 
                  className="w-full h-full object-cover" 
                  onError={(e) => { e.target.src = '/logo.png'; }} 
                />
              </div>
              <div className="absolute inset-0 bg-[#8C6208] rounded-full blur-md opacity-25 scale-125 animate-pulse"></div>
              <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-emerald-500 border-2 border-white rounded-full z-20"></div>
            </div>
            <h3 className="text-base font-black text-[#302621]">Asa</h3>
            <p className="text-[10px] text-emerald-600 font-bold tracking-wider uppercase">Asisten Pengiriman Satala</p>
          </div>

          {/* Speech Bubble dari Asa */}
          <div className="bg-[#FFFDF9] border border-[#E6D5B8] p-3.5 rounded-2xl shadow-xs mb-5 relative text-center">
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-3.5 h-3.5 bg-[#FFFDF9] border-t border-l border-[#E6D5B8] rotate-45"></div>
            <p className="text-xs text-[#302621] font-medium leading-relaxed relative z-10">
              "Halo! Dokumen apa yang ingin dicetak untuk pengiriman <strong>{brandName}</strong> ({destName} · via {ekspedisi})?"
            </p>
          </div>

          {/* 3 Opsi Pilihan Dokumen */}
          <div className="space-y-2.5">
            {/* Opsi 1: Cetak Surat Jalan Saja */}
            <button
              type="button"
              onClick={() => {
                onClose();
                onSelectSuratJalan?.();
              }}
              className="w-full text-left p-3.5 rounded-2xl border border-slate-200 hover:border-[#8C6208] bg-white hover:bg-[#F9F5EC]/60 transition-all flex items-center justify-between group shadow-2xs hover:shadow-xs cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-50 border border-orange-100 text-orange-600 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-800 group-hover:text-[#8C6208]">
                    Cetak Surat Jalan Saja
                  </h4>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Surat jalan resmi dengan tanda tangan PIC & stempel Satala
                  </p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#8C6208] group-hover:translate-x-0.5 transition-all shrink-0 ml-2" />
            </button>

            {/* Opsi 2: Cetak Label Dus Saja */}
            <button
              type="button"
              onClick={() => {
                onClose();
                onSelectLabelDus?.();
              }}
              className="w-full text-left p-3.5 rounded-2xl border border-slate-200 hover:border-[#8C6208] bg-white hover:bg-[#F9F5EC]/60 transition-all flex items-center justify-between group shadow-2xs hover:shadow-xs cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                  <Tag className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-800 group-hover:text-[#8C6208]">
                    Cetak Label Dus Saja
                  </h4>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Stiker identitas kardus (format lembar A4 landscape siap potong)
                  </p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#8C6208] group-hover:translate-x-0.5 transition-all shrink-0 ml-2" />
            </button>

            {/* Opsi 3: Cetak Keduanya (Lengkap) */}
            <button
              type="button"
              onClick={() => {
                onClose();
                onSelectBoth?.();
              }}
              className="w-full text-left p-3.5 rounded-2xl border-2 border-[#8C6208] bg-[#F9F5EC]/80 hover:bg-[#F2E8D5] transition-all flex items-center justify-between group shadow-xs cursor-pointer relative overflow-hidden"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#8C6208] text-white flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform shadow-xs">
                  <Boxes className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <h4 className="text-xs font-black text-[#302621]">
                      Cetak Keduanya (Lengkap)
                    </h4>
                    <span className="text-[9px] bg-[#8C6208] text-white font-bold px-1.5 py-0.2 rounded-md">
                      Rekomendasi
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-600 mt-0.5">
                    Surat Jalan Resmi + Seluruh Lembar Stiker Kardus lengkap
                  </p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-[#8C6208] group-hover:translate-x-1 transition-all shrink-0 ml-2" />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-50 px-6 py-3 border-t border-slate-100 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-medium text-slate-500 hover:text-slate-800 transition-colors"
          >
            Batal
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
